import { useMemo, useRef, useCallback } from 'react';
import type { UseComputedStyleResult } from './useComputedStyle';
import type { CanvasDimensions } from '../utils/canvas-utils';
import type { TextParticlesProps } from '../../TextParticles.types';

type UseImageLoaderParams = {
  canvasDimensions: CanvasDimensions;
  computedStyle: UseComputedStyleResult;
  text: string;
  textDirection: TextParticlesProps['textDirection'];
};

export const useImageLoader = ({
  canvasDimensions,
  computedStyle,
  text,
  textDirection,
}: UseImageLoaderParams) => {
  const previousCacheRef = useRef<string>('');

  const getImageCache = useCallback(
    (
      _canvasDimensions: CanvasDimensions,
      _computedStyle: UseComputedStyleResult,
      _text: string,
      _textDirection: TextParticlesProps['textDirection'],
    ) => {
      if (!_canvasDimensions || !_computedStyle) {
        return '';
      }

      const cacheData = {
        canvasDimensions: {
          offscreenWidth: _canvasDimensions.offscreenWidth,
          offscreenHeight: _canvasDimensions.offscreenHeight,
          devicePixelRatio: _canvasDimensions.devicePixelRatio,
          displayWidth: _canvasDimensions.displayWidth,
          displayHeight: _canvasDimensions.displayHeight,
        },
        computedStyle: {
          color: _computedStyle.color,
          font: _computedStyle.font,
          letterSpacing: _computedStyle.letterSpacing,
          textTransform: _computedStyle.textTransform,
        },
        text: _text,
        textDirection: _textDirection,
      };

      return JSON.stringify(cacheData);
    },
    [],
  );

  const imageBitmap = useMemo(() => {
    if (
      !canvasDimensions ||
      canvasDimensions.displayWidth === 0 ||
      canvasDimensions.displayHeight === 0 ||
      !computedStyle
    ) {
      return null;
    }

    const currentCache = getImageCache(
      canvasDimensions,
      computedStyle,
      text,
      textDirection,
    );
    const previousCache = previousCacheRef.current;

    if (previousCache === currentCache) {
      return null;
    }

    const analysisCanvas = new OffscreenCanvas(
      canvasDimensions.offscreenWidth,
      canvasDimensions.offscreenHeight,
    );
    const analysisContext = analysisCanvas.getContext('2d', {
      willReadFrequently: true,
    }) as OffscreenCanvasRenderingContext2D;

    if (!analysisContext) {
      return null;
    }

    analysisContext.scale(
      canvasDimensions.devicePixelRatio,
      canvasDimensions.devicePixelRatio,
    );
    analysisContext.textBaseline = 'middle';
    analysisContext.textAlign = 'center';
    analysisContext.fillStyle = computedStyle.color;
    analysisContext.font = computedStyle.font;
    analysisContext.direction = textDirection;
    // letterSpacing is not part of the official CanvasRenderingContext2D interface
    (analysisContext as any).letterSpacing = computedStyle.letterSpacing;
    // Canvas doesn't support text-transform, so we need to transform the text here
    const transformedText = transformText(text, computedStyle.textTransform);
    analysisContext.fillText(
      transformedText,
      canvasDimensions.displayWidth / 2,
      canvasDimensions.displayHeight / 2,
    );

    previousCacheRef.current = currentCache;
    return analysisCanvas.transferToImageBitmap();
  }, [computedStyle, canvasDimensions, text, textDirection, getImageCache]);

  return imageBitmap;
};

// Not exporting this on purpose, use CSS text-transform where available
const transformText = (text: string, textTransform: string) => {
  if (textTransform === 'uppercase') {
    return text.toUpperCase();
  }
  if (textTransform === 'lowercase') {
    return text.toLowerCase();
  }
  if (textTransform === 'capitalize') {
    // Split by common separators and capitalize the first letter of each part
    return text.replace(/[^\s\-_.,;:!?()[\]{}/\\|+=&@#$%^*~`'"]+/g, word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }
  return text;
};
